import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ALERT_AFTER_CHOICES,
  buildSelection,
  HISTORY_PRESETS,
  historyDays,
  includesAll,
  looksAbsolutePath,
  matchHistoryPreset,
  matchSchedulePreset,
  MEDIA_EXTENSIONS,
  PERSONAL_FOLDERS,
  scheduleForPreset,
  suggestedMaxAgeHours,
  TEMP_FILE_PATTERNS,
  toggleAll,
  type HistoryPreset,
  type PersonalFolder,
  type PolicySpec,
  type SchedulePreset,
} from '@zerogap/shared';
import { get, post } from '../api';
import { locale, msg, tr } from '../i18n';
import { useTenant } from '../session';
import { bytes, Button, Field, friendlyError, HelpTip, Icons, OptionCards, Segmented, TechDetails } from '../ui';

/**
 * The parts of a backup plan, in plain words, shared by the "Create a backup plan" wizard and the plan page:
 * what to back up, when, how long to keep it, and the advanced settings most businesses never touch.
 */

type Schedule = PolicySpec['schedule'];
type Retention = PolicySpec['retention'];
type Selection = PolicySpec['selection'];
export type SpecChange = (s: PolicySpec) => void;

// ───────── text ─────────

export const FOLDER_LABEL: Record<PersonalFolder, string> = {
  Documents: msg('Documents'),
  Desktop: msg('Desktop'),
  Pictures: msg('Pictures'),
  Downloads: msg('Downloads'),
  Music: msg('Music'),
  Videos: msg('Videos'),
};

/** Keeps a path or time range readable inside a right-to-left sentence. */
export const ltr = (text: string) => `\u2066${text}\u2069`;

export const listJoin = (items: string[]) => new Intl.ListFormat(locale(), { style: 'long', type: 'conjunction' }).format(items);
const num = (n: number) => n.toLocaleString(locale(), { maximumFractionDigits: 1 });
// 4 January 2026 was a Sunday; weekday 0 = Sunday, like the schedule.
export const weekday = (d: number, width: 'long' | 'short') => new Date(Date.UTC(2026, 0, 4 + d, 12)).toLocaleDateString(locale(), { weekday: width, timeZone: 'UTC' });

export function whatText(sel: Selection): string {
  const others = sel.includePaths.length;
  if (sel.mode === 'profiles' && sel.profileFolders.length) {
    const folders = listJoin(sel.profileFolders.map((f) => tr(FOLDER_LABEL[f])));
    const base = sel.profiles === 'all' ? tr('{folders} of every user', { folders }) : tr('{folders} of {n} chosen users', { folders, n: sel.profiles.length });
    if (!others) return base;
    return others === 1 ? tr('{base}, plus one more folder', { base }) : tr('{base}, plus {n} more folders', { base, n: others });
  }
  if (others === 0) return tr('No folders chosen yet');
  if (others === 1) return tr('The folder {path}', { path: ltr(sel.includePaths[0]!.path) });
  return tr('{n} folders, including {path}', { n: others, path: ltr(sel.includePaths[0]!.path) });
}

export function whenText(s: Schedule): string {
  const preset = matchSchedulePreset(s);
  if (preset === 'several-daily') return tr('Several times a day (every 3 hours, 08:00–21:00)');
  if (preset === 'daily') return tr('Every day at {time}', { time: s.times![0]! });
  if (preset === 'weekly') return tr('Every {day} at {time}', { day: weekday(s.days![0]!, 'long'), time: s.times![0]! });
  const limited = s.days?.length && s.days.length < 7 ? tr('only on {days}', { days: listJoin(s.days.map((d) => weekday(d, 'short'))) }) : '';
  let main: string;
  if (s.kind === 'interval') {
    const h = (s.everyMinutes ?? 60) / 60;
    main = h === 1 ? tr('Every hour') : tr('Every {n} hours', { n: num(h) });
    if (s.windowStart || s.windowEnd) main = `${main} ${tr('between {from} and {to}', { from: s.windowStart ?? '00:00', to: s.windowEnd ?? '24:00' })}`;
  } else if (s.kind === 'weekly') {
    return tr('Every {days} at {times}', { days: listJoin((s.days ?? []).map((d) => weekday(d, 'long'))), times: listJoin(s.times ?? []) });
  } else {
    main = tr('Every day at {times}', { times: listJoin(s.times ?? []) });
  }
  return limited ? `${main}, ${limited}` : main;
}

export function spanText(days: number): string {
  if (days < 28) return days === 1 ? tr('1 day') : tr('{n} days', { n: days });
  if (days < 360) {
    const m = Math.max(1, Math.round(days / 30.4));
    return m === 1 ? tr('1 month') : tr('{n} months', { n: m });
  }
  const y = Math.round(days / 365);
  return y === 1 ? tr('1 year') : tr('{n} years', { n: y });
}

export const keepText = (r: Retention) => tr('Keeps about {span} of history', { span: spanText(historyDays(r)) });

/** "You can get files back as they were: at any backup in the last 7 days, from any day in the last 30 days, …" */
export function keepExplain(r: Retention): string {
  const parts: string[] = [];
  if (r.intradayDays) parts.push(tr('at any backup in the last {n} days', { n: r.intradayDays }));
  if (r.daily) parts.push(tr('from any day in the last {n} days', { n: r.daily }));
  if (r.weekly) parts.push(tr('from any week in the last {n} weeks', { n: r.weekly }));
  if (r.monthly) parts.push(tr('from any month in the last {n} months', { n: r.monthly }));
  if (r.yearly) parts.push(tr('from any year in the last {n} years', { n: r.yearly }));
  if (!parts.length) return tr('Only the latest {n} backups are kept.', { n: r.keepLast });
  return tr('You can get files back as they were {parts}.', { parts: listJoin(parts) });
}

function alertLabel(h: number): string {
  if (h === 168) return tr('1 week');
  if (h % 24 === 0) return h === 24 ? tr('1 day') : h === 48 ? tr('2 days') : tr('{n} days', { n: h / 24 });
  return tr('{n} hours', { n: h });
}

/** Three plain lines: what, when, how long. */
export function PlanLines({ spec }: { spec: PolicySpec }) {
  return (
    <ul className="plan-lines">
      <li><Icons.folder /><span>{whatText(spec.selection)}</span></li>
      <li><Icons.clock /><span>{whenText(spec.schedule)}</span></li>
      <li><Icons.history /><span>{keepText(spec.retention)}</span></li>
    </ul>
  );
}

/** A name to start from, based on what the plan backs up. */
export function suggestedName(spec: PolicySpec): string {
  const sel = spec.selection;
  if (sel.mode === 'paths' && sel.includePaths.length) {
    const last = sel.includePaths[0]!.path.replace(/[\\/]+$/, '').split(/[\\/]/).pop();
    if (last && !last.endsWith(':')) return last;
  }
  return tr('Personal files');
}

/** Upcoming backup times for an unsaved plan, in the viewer's time zone (debounced). */
export function useNextRuns(spec: PolicySpec) {
  const t = useTenant();
  const [runs, setRuns] = useState<string[]>([]);
  useEffect(() => {
    const h = setTimeout(async () => {
      try {
        const r = await post(`/api/t/${t.tenantId}/policies/validate`, { spec, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
        setRuns(r.nextRuns ?? []);
      } catch {
        setRuns([]);
      }
    }, 300);
    return () => clearTimeout(h);
  }, [JSON.stringify(spec.schedule), t.tenantId]); // eslint-disable-line react-hooks/exhaustive-deps
  return runs;
}

function runText(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(Date.now() + 86_400_000);
  const time = d.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return tr('today {time}', { time });
  if (d.toDateString() === tomorrow.toDateString()) return tr('tomorrow {time}', { time });
  return d.toLocaleString(locale(), { weekday: 'short', hour: '2-digit', minute: '2-digit' });
}

// ───────── what to back up ─────────

export function WhatSection({ spec, onChange, disabled, previewable }: { spec: PolicySpec; onChange: SpecChange; disabled?: boolean; previewable?: boolean }) {
  const sel = spec.selection;
  const personal: PersonalFolder[] = sel.mode === 'profiles' ? sel.profileFolders : [];
  const [rows, setRows] = useState<string[]>(() => sel.includePaths.map((r) => r.path));
  const update = (nextPersonal: readonly PersonalFolder[], nextRows: string[]) => {
    setRows(nextRows);
    onChange({ ...spec, selection: buildSelection(sel, nextPersonal, nextRows) });
  };
  const setSel = (patch: Partial<Selection>) => onChange({ ...spec, selection: { ...sel, ...patch } });
  const nothing = personal.length === 0 && !rows.some((r) => r.trim());
  const maxGb = sel.maxFileSizeBytes ? Math.round((sel.maxFileSizeBytes / 1024 ** 3) * 10) / 10 : 0;
  return (
    <fieldset disabled={disabled} className="plain-fieldset">
      <div className="section-title">{tr("Each person's files")}</div>
      <div className="check-grid">
        {PERSONAL_FOLDERS.map((f) => (
          <label key={f} className="check">
            <input type="checkbox" checked={personal.includes(f)} onChange={(e) => update(e.target.checked ? [...personal, f] : personal.filter((x) => x !== f), rows)} />
            <span>{tr(FOLDER_LABEL[f])}</span>
          </label>
        ))}
      </div>
      <p className="small muted" style={{ margin: '8px 0 18px' }}>
        {personal.length === 0 ? '' : sel.profiles === 'all' ? tr('For every person who signs in to the computer.') : tr('Only for the users chosen under Advanced settings.')}
      </p>

      <div className="section-title">
        {tr('Other folders')}
        <HelpTip>{tr('For example, a shared company folder on a second drive or on a server. Type its full location, like D:\\Shared\\Accounts.')}</HelpTip>
      </div>
      <div className="folder-rows">
        {rows.map((r, i) => (
          <div key={i} className="folder-row">
            <div>
              <input
                type="text"
                className="mono"
                dir="ltr"
                value={r}
                placeholder={i === 0 ? 'D:\\Shared\\Accounts' : ''}
                aria-label={tr('Folder location')}
                onChange={(e) => update(personal, rows.map((x, j) => (j === i ? e.target.value : x)))}
              />
              {r.trim() && !looksAbsolutePath(r) && <div className="err small">{tr('Start with a drive letter (like D:\\) or a network address (like \\\\server\\share). On Linux, start with /.')}</div>}
            </div>
            <Button size="sm" variant="ghost" aria-label={tr('Remove this folder')} onClick={() => update(personal, rows.filter((_, j) => j !== i))}>
              <Icons.x width={16} />
            </Button>
          </div>
        ))}
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        <Button size="sm" onClick={() => setRows([...rows, ''])}><Icons.plus width={14} />{tr('Add a folder')}</Button>
      </div>
      <p className="small muted" style={{ margin: '8px 0 0' }}>{tr('Network drives such as S: are not visible to the backup app. Use the network address instead, like \\\\server\\share.')}</p>
      {nothing && <div className="notice warn" style={{ marginTop: 14 }}>{tr('Choose at least one folder to back up.')}</div>}

      <TechDetails summary={tr('Skip some files (optional)')}>
        <label className="check" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={includesAll(sel.excludeExtensions, MEDIA_EXTENSIONS)} onChange={(e) => setSel({ excludeExtensions: toggleAll(sel.excludeExtensions, MEDIA_EXTENSIONS, e.target.checked) })} />
          <span>{tr('Skip videos and music')}</span>
        </label>
        <label className="check" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={includesAll(sel.excludeGlobs, TEMP_FILE_PATTERNS)} onChange={(e) => setSel({ excludeGlobs: toggleAll(sel.excludeGlobs, TEMP_FILE_PATTERNS, e.target.checked) })} />
          <span>{tr('Skip temporary files')}</span>
        </label>
        <div className="row" style={{ alignItems: 'center' }}>
          <label className="check">
            <input type="checkbox" checked={!!sel.maxFileSizeBytes} onChange={(e) => setSel({ maxFileSizeBytes: e.target.checked ? 2 * 1024 ** 3 : undefined })} />
            <span>{tr('Skip files bigger than')}</span>
          </label>
          {!!sel.maxFileSizeBytes && (
            <span className="row" style={{ gap: 6 }}>
              <input type="number" min={0.1} step={0.5} value={maxGb} style={{ width: 90 }} aria-label={tr('Size in GB')}
                onChange={(e) => setSel({ maxFileSizeBytes: Number(e.target.value) > 0 ? Math.round(Number(e.target.value) * 1024 ** 3) : undefined })} />
              <span>GB</span>
            </span>
          )}
        </div>
      </TechDetails>
      {previewable && <CheckOnComputer selection={sel} />}
    </fieldset>
  );
}

interface PreviewResult {
  missing: string[] | null;
  files: number;
  bytes: number;
  included: { path: string; size: number }[] | null;
  excluded: { path: string; reason: string }[] | null;
  truncated: boolean;
}

/** Runs the (unsaved) choice on one computer and reports, in plain words, how much it covers. */
function CheckOnComputer({ selection }: { selection: Selection }) {
  const t = useTenant();
  const devices = useQuery({ queryKey: ['devices-active', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/devices?status=active&limit=200`) });
  const [deviceId, setDeviceId] = useState('');
  const [state, setState] = useState<'idle' | 'waiting' | 'done' | 'failed'>('idle');
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list: { id: string; display_name: string }[] = devices.data?.devices ?? [];
  const chosen = deviceId || list[0]?.id || '';
  const name = list.find((d) => d.id === chosen)?.display_name ?? '';
  const run = async () => {
    setState('waiting');
    setResult(null);
    setError(null);
    try {
      const { commandId } = await post(`/api/t/${t.tenantId}/devices/${chosen}/selection-preview`, { selection });
      const deadline = Date.now() + 10 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 1500));
        const { command } = await get(`/api/t/${t.tenantId}/devices/${chosen}/commands/${commandId}`);
        if (command.state === 'completed') {
          setResult(command.result as PreviewResult);
          setState('done');
          return;
        }
        if (command.state === 'failed' || command.state === 'expired') {
          setError(command.state === 'expired' ? tr('The computer did not answer. Is it switched on and connected to the internet?') : tr('The computer could not check these folders.'));
          setState('failed');
          return;
        }
      }
    } catch (e) {
      setError(friendlyError(e));
      setState('failed');
    }
  };
  return (
    <div className="preview-box">
      <div className="section-title">
        {tr('Check on a computer')}
        <HelpTip>{tr('See how much this would back up on one of your computers. Nothing is backed up yet, and nothing is changed.')}</HelpTip>
      </div>
      {list.length === 0 ? (
        <p className="small muted" style={{ margin: 0 }}>{tr('Once a computer is connected, you can check here what this would back up on it.')}</p>
      ) : (
        <div className="row" style={{ alignItems: 'center' }}>
          <select value={chosen} onChange={(e) => setDeviceId(e.target.value)} aria-label={tr('Computer to check')} style={{ maxWidth: 260 }}>
            {list.map((d) => <option key={d.id} value={d.id}>{d.display_name}</option>)}
          </select>
          <Button busy={state === 'waiting'} onClick={run}>{state === 'waiting' ? tr('Asking the computer…') : tr('Check')}</Button>
        </div>
      )}
      {state === 'waiting' && <p className="small muted" style={{ margin: '8px 0 0' }}>{tr('This usually takes up to a minute.')}</p>}
      {error && <div className="notice bad" style={{ marginTop: 12 }}>{error}</div>}
      {result && (
        <div style={{ marginTop: 12 }} className="stack">
          <div className="notice ok" style={{ margin: 0 }}>
            <strong>{tr('On {name} this is {files} files ({size}).', { name, files: result.files.toLocaleString(locale()), size: bytes(result.bytes) })}</strong>
            {result.truncated && <span>{tr('The computer stopped counting after 45 seconds, so the real amount is larger.')}</span>}
          </div>
          {!!result.missing?.length && (
            <div className="notice warn" style={{ margin: 0 }}>
              <strong>{tr('Not found on {name}:', { name })}</strong>
              {result.missing.map((m) => <div key={m} className="mono small" dir="ltr">{m}</div>)}
              <div className="small">{tr('Those folders are skipped on this computer. Check the spelling, or ignore this if they exist only on other computers.')}</div>
            </div>
          )}
          <TechDetails summary={tr('Show examples')}>
            <div className="small" style={{ fontWeight: 600 }}>{tr('Included')}</div>
            <ul className="mono small preview-list" dir="ltr">{(result.included ?? []).map((f) => <li key={f.path}>{f.path} <span className="muted">{bytes(f.size)}</span></li>)}</ul>
            <div className="small" style={{ fontWeight: 600 }}>{tr('Skipped')}</div>
            <ul className="mono small preview-list" dir="ltr">{(result.excluded ?? []).map((f) => <li key={f.path}>{f.path} <span className="muted">— {f.reason}</span></li>)}</ul>
          </TechDetails>
        </div>
      )}
    </div>
  );
}

// ───────── when ─────────

export function WhenSection({ spec, onChange, disabled }: { spec: PolicySpec; onChange: SpecChange; disabled?: boolean }) {
  const s = spec.schedule;
  const [mode, setMode] = useState<SchedulePreset>(() => matchSchedulePreset(s));
  const runs = useNextRuns(spec);
  const setSchedule = (schedule: Schedule) => {
    // Keep "warn me after" in step with the schedule, unless someone chose it by hand.
    const auto = spec.alerting.maxAgeHours === suggestedMaxAgeHours(s);
    onChange({ ...spec, schedule, alerting: auto ? { ...spec.alerting, maxAgeHours: suggestedMaxAgeHours(schedule) } : spec.alerting });
  };
  const pick = (m: SchedulePreset) => {
    setMode(m);
    if (m !== 'custom') setSchedule(scheduleForPreset(m, s));
  };
  const time = s.times?.[0] ?? '12:00';
  const suggested = suggestedMaxAgeHours(s);
  const choices = [...new Set<number>([...ALERT_AFTER_CHOICES, spec.alerting.maxAgeHours])].sort((a, b) => a - b);
  const timeInput = (onTime: (v: string) => void) => (
    <input type="time" value={time} onChange={(e) => e.target.value && onTime(e.target.value)} style={{ width: 130 }} aria-label={tr('Time')} />
  );
  return (
    <fieldset disabled={disabled} className="plain-fieldset">
      <OptionCards
        name="when"
        value={mode}
        onChange={pick}
        disabled={disabled}
        options={[
          { key: 'several-daily', title: tr('Several times a day'), desc: tr('Every 3 hours from 08:00 to 21:00. Recommended: little work is ever lost.') },
          {
            key: 'daily',
            title: tr('Once a day'),
            desc: tr('Choose a time when the computers are usually on.'),
            extra: <div className="row">{tr('At')} {timeInput((v) => setSchedule(scheduleForPreset('daily', s, { time: v })))}</div>,
          },
          {
            key: 'weekly',
            title: tr('Once a week'),
            extra: (
              <div className="row">
                <select value={s.days?.[0] ?? 1} style={{ width: 150 }} aria-label={tr('Day')} onChange={(e) => setSchedule(scheduleForPreset('weekly', s, { day: Number(e.target.value) }))}>
                  {[0, 1, 2, 3, 4, 5, 6].map((d) => <option key={d} value={d}>{weekday(d, 'long')}</option>)}
                </select>
                {tr('at')} {timeInput((v) => setSchedule(scheduleForPreset('weekly', s, { time: v })))}
              </div>
            ),
          },
          { key: 'custom', title: tr('Custom'), desc: tr('Choose exact times and days.'), extra: <CustomSchedule s={s} onChange={setSchedule} /> },
        ]}
      />
      <div style={{ marginTop: 16 }}>
        <Field label={<>{tr('Warn me if a computer goes without a backup for')} <HelpTip>{tr('Computers that are switched off simply catch up later. You get an alert only when a computer has gone this long without a backup.')}</HelpTip></>}>
          <select value={spec.alerting.maxAgeHours} style={{ maxWidth: 260 }} onChange={(e) => onChange({ ...spec, alerting: { ...spec.alerting, maxAgeHours: Number(e.target.value) } })}>
            {choices.map((h) => <option key={h} value={h}>{alertLabel(h)}{h === suggested ? ` (${tr('recommended')})` : ''}</option>)}
          </select>
        </Field>
      </div>
      {runs.length > 0 && (
        <p className="small muted" style={{ margin: 0 }}>
          {tr('Next backups (your time): {runs}', { runs: runs.slice(0, 4).map(runText).join(' · ') })}
        </p>
      )}
    </fieldset>
  );
}

function CustomSchedule({ s, onChange }: { s: Schedule; onChange: (s: Schedule) => void }) {
  const set = (patch: Partial<Schedule>) => onChange({ ...s, ...patch });
  const times = s.times?.length ? s.times : ['12:00'];
  const kind = (k: Schedule['kind']) => {
    if (k === 'interval') set({ kind: k, everyMinutes: s.everyMinutes ?? 180, windowStart: s.windowStart ?? '08:00', windowEnd: s.windowEnd ?? '21:00', times: undefined });
    else set({ kind: k, times, everyMinutes: undefined, windowStart: undefined, windowEnd: undefined, ...(k === 'weekly' && !s.days?.length ? { days: [1] } : {}) });
  };
  const hours = [1, 2, 3, 4, 6, 8, 12];
  const every = (s.everyMinutes ?? 180) / 60;
  const toggleDay = (d: number, on: boolean) => {
    const days = on ? [...(s.days ?? []), d].sort() : (s.days ?? []).filter((x) => x !== d);
    set({ days: days.length ? days : undefined });
  };
  return (
    <div className="stack" style={{ gap: 12 }}>
      <Segmented
        value={s.kind}
        onChange={kind}
        options={[
          { key: 'interval', label: tr('Every few hours') },
          { key: 'daily', label: tr('At set times') },
          { key: 'weekly', label: tr('On certain days') },
        ]}
      />
      {s.kind === 'interval' ? (
        <div className="row">
          {tr('Every')}
          <select value={every} style={{ width: 90 }} aria-label={tr('Hours between backups')} onChange={(e) => set({ everyMinutes: Number(e.target.value) * 60 })}>
            {[...new Set([...hours, every])].sort((a, b) => a - b).map((h) => <option key={h} value={h}>{num(h)}</option>)}
          </select>
          {tr('hours, from')}
          <input type="time" value={s.windowStart ?? '00:00'} style={{ width: 120 }} aria-label={tr('From')} onChange={(e) => set({ windowStart: e.target.value || undefined })} />
          {tr('until')}
          <input type="time" value={s.windowEnd ?? '23:59'} style={{ width: 120 }} aria-label={tr('Until')} onChange={(e) => set({ windowEnd: e.target.value || undefined })} />
        </div>
      ) : (
        <div className="row">
          {times.map((tm, i) => (
            <span key={i} className="row" style={{ gap: 4 }}>
              <input type="time" value={tm} style={{ width: 120 }} aria-label={tr('Time')} onChange={(e) => e.target.value && set({ times: times.map((x, j) => (j === i ? e.target.value : x)) })} />
              {times.length > 1 && <Button size="sm" variant="ghost" aria-label={tr('Remove this time')} onClick={() => set({ times: times.filter((_, j) => j !== i) })}><Icons.x width={14} /></Button>}
            </span>
          ))}
          <Button size="sm" onClick={() => set({ times: [...times, '17:00'] })}><Icons.plus width={14} />{tr('Add a time')}</Button>
        </div>
      )}
      <div>
        <div className="small" style={{ marginBottom: 6 }}>{s.kind === 'weekly' ? tr('On these days') : tr('Only on these days (leave all unticked for every day)')}</div>
        <div className="row">
          {[0, 1, 2, 3, 4, 5, 6].map((d) => (
            <label key={d} className="check"><input type="checkbox" checked={(s.days ?? []).includes(d)} onChange={(e) => toggleDay(d, e.target.checked)} />{weekday(d, 'short')}</label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ───────── how long to keep ─────────

export function KeepSection({ spec, onChange, disabled }: { spec: PolicySpec; onChange: SpecChange; disabled?: boolean }) {
  const r = spec.retention;
  const [mode, setMode] = useState<HistoryPreset>(() => matchHistoryPreset(r));
  const setR = (retention: Retention) => onChange({ ...spec, retention });
  const pick = (m: HistoryPreset) => {
    setMode(m);
    if (m !== 'custom') setR(HISTORY_PRESETS[m]);
  };
  const n = (k: keyof Retention, label: string, min = 0) => (
    <Field label={label}>
      <input type="number" min={min} value={r[k]} onChange={(e) => setR({ ...r, [k]: Math.max(min, Number(e.target.value) || 0) })} />
    </Field>
  );
  return (
    <fieldset disabled={disabled} className="plain-fieldset">
      <OptionCards
        name="keep"
        value={mode}
        onChange={pick}
        disabled={disabled}
        options={[
          { key: '1m', title: tr('1 month') },
          { key: '3m', title: tr('3 months'), desc: tr('Recommended for most businesses.') },
          { key: '1y', title: tr('1 year') },
          { key: '7y', title: tr('7 years'), desc: tr('For business records that must be kept for years.') },
          {
            key: 'custom',
            title: tr('Custom'),
            extra: (
              <div className="grid g3">
                {n('intradayDays', tr('Keep every backup for (days)'))}
                {n('daily', tr('Then one per day for (days)'))}
                {n('weekly', tr('One per week for (weeks)'))}
                {n('monthly', tr('One per month for (months)'))}
                {n('yearly', tr('One per year for (years)'))}
                {n('keepLast', tr('Always keep at least the last (backups)'), 1)}
              </div>
            ),
          },
        ]}
      />
      <p style={{ margin: '14px 0 4px' }}>{keepExplain(r)}</p>
      <p className="small muted" style={{ margin: 0 }}>{tr('Longer history uses more storage space. Days when a computer was off do not count.')}</p>
    </fieldset>
  );
}

// ───────── advanced ─────────

function Lines({ value, onChange, placeholder }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [text, setText] = useState(value.join('\n'));
  useEffect(() => setText(value.join('\n')), [value.join('\n')]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <textarea value={text} placeholder={placeholder} dir="ltr" className="mono" spellCheck={false}
      onChange={(e) => setText(e.target.value)} onBlur={() => onChange(text.split('\n').map((x) => x.trim()).filter(Boolean))} />
  );
}

const zones = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['UTC'];
  }
})();

export function AdvancedSection({ spec, onChange, disabled, children }: { spec: PolicySpec; onChange: SpecChange; disabled?: boolean; children?: ReactNode }) {
  const s = spec.schedule;
  const l = spec.limits;
  const sel = spec.selection;
  const setL = (patch: Partial<PolicySpec['limits']>) => onChange({ ...spec, limits: { ...l, ...patch } });
  const setS = (patch: Partial<Schedule>) => onChange({ ...spec, schedule: { ...s, ...patch } });
  const setSel = (patch: Partial<Selection>) => onChange({ ...spec, selection: { ...sel, ...patch } });
  const [someUsers, setSomeUsers] = useState(sel.profiles !== 'all');
  const exts = (v: string) => v.split(',').map((x) => x.trim().replace(/^\./, '')).filter(Boolean);
  return (
    <TechDetails summary={tr('Advanced settings')}>
      <fieldset disabled={disabled} className="plain-fieldset">
        <p className="small muted" style={{ marginTop: 0 }}>{tr('Most businesses never need to change these.')}</p>
        <div className="grid g2">
          <Field label={tr('Clock used for the schedule')}>
            <select value={s.timezone} onChange={(e) => setS({ timezone: e.target.value })}>
              <option value="device">{tr("Each computer's own clock (recommended)")}</option>
              {zones.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </Field>
          <Field label={tr('Spread start times over (minutes)')} hint={tr("So that all computers don't start at the same moment.")}>
            <input type="number" min={0} max={120} value={s.jitterMinutes} onChange={(e) => setS({ jitterMinutes: Math.min(120, Math.max(0, Number(e.target.value) || 0)) })} />
          </Field>
          <Field label={tr('Upload speed limit (Mbit/s, 0 = no limit)')}>
            <input type="number" min={0} value={l.bandwidthKbps / 1000} onChange={(e) => setL({ bandwidthKbps: Math.max(0, Math.round(Number(e.target.value) * 1000)) })} />
          </Field>
          <Field label={tr('Stop a backup that runs longer than (hours)')}>
            <input type="number" min={1} value={Math.round(l.maxDurationMinutes / 60)} onChange={(e) => setL({ maxDurationMinutes: Math.max(10, Math.round(Number(e.target.value) * 60)) })} />
          </Field>
        </div>
        <label className="check" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={s.catchUp === 'once'} onChange={(e) => setS({ catchUp: e.target.checked ? 'once' : 'none' })} />
          <span>{tr('If a backup was missed because the computer was off, run it as soon as the computer is back on')}</span>
        </label>
        <label className="check" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={l.onBattery === 'skip'} onChange={(e) => setL({ onBattery: e.target.checked ? 'skip' : 'run' })} />
          <span>{tr('Wait while a laptop runs on battery')}</span>
        </label>
        <label className="check" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={l.meteredNetwork === 'skip'} onChange={(e) => setL({ meteredNetwork: e.target.checked ? 'skip' : 'run' })} />
          <span>{tr('Wait while on a mobile or limited internet connection')}</span>
        </label>
        <label className="check" style={{ marginBottom: 16 }}>
          <input type="checkbox" checked={l.cpuPriority === 'low'} onChange={(e) => setL({ cpuPriority: e.target.checked ? 'low' : 'normal' })} />
          <span>{tr('Run backups quietly in the background (lower priority)')}</span>
        </label>
        <Field label={tr('Back up files that are open in other programs (Windows)')}>
          <Segmented
            value={spec.vss}
            onChange={(v) => onChange({ ...spec, vss: v })}
            options={[
              { key: 'preferred', label: tr('When possible (recommended)') },
              { key: 'required', label: tr('Always') },
              { key: 'off', label: tr('Never') },
            ]}
          />
        </Field>
        <div className="grid g2">
          <Field label={tr('Check a sample of stored backups every (days, 0 = never)')}>
            <input type="number" min={0} value={spec.verification.sampleEveryDays} onChange={(e) => onChange({ ...spec, verification: { ...spec.verification, sampleEveryDays: Math.max(0, Number(e.target.value) || 0) } })} />
          </Field>
          <Field label={tr('Do a test restore automatically every (days, 0 = never)')}>
            <input type="number" min={0} value={spec.verification.restoreDrillEveryDays} onChange={(e) => onChange({ ...spec, verification: { ...spec.verification, restoreDrillEveryDays: Math.max(0, Number(e.target.value) || 0) } })} />
          </Field>
        </div>
        <Field label={tr('Whose personal files')}>
          <Segmented
            value={someUsers ? 'some' : 'all'}
            onChange={(v) => {
              setSomeUsers(v === 'some');
              if (v === 'all') setSel({ profiles: 'all' });
            }}
            options={[{ key: 'all', label: tr('Everyone on the computer') }, { key: 'some', label: tr('Only these users') }]}
          />
        </Field>
        {someUsers && (
          <Field label={tr('Windows or Linux user names, one per line')} hint={tr('Leave empty to back up everyone.')}>
            <Lines value={sel.profiles === 'all' ? [] : sel.profiles} onChange={(v) => setSel({ profiles: v.length ? v : 'all' })} />
          </Field>
        )}
        <div className="section-title" style={{ marginTop: 8 }}>{tr('File rules for IT staff')}</div>
        <div className="grid g2">
          <Field label={tr('Folders to leave out, one per line')}>
            <Lines value={sel.excludePaths.map((r) => r.path)} onChange={(v) => setSel({ excludePaths: v.map((path) => ({ path })) })} />
          </Field>
          <Field label={tr('Name patterns to leave out, one per line')} hint={tr('For example *.tmp or node_modules. A * matches any characters.')}>
            <Lines value={sel.excludeGlobs} onChange={(v) => setSel({ excludeGlobs: v })} />
          </Field>
          <Field label={tr('Only these file types (optional)')} hint={tr('Comma separated, for example: docx, xlsx, pdf')}>
            <input type="text" className="mono" dir="ltr" value={sel.includeExtensions.join(', ')} onChange={(e) => setSel({ includeExtensions: exts(e.target.value) })} />
          </Field>
          <Field label={tr('Leave out these file types')} hint={tr('Comma separated, for example: iso, vhdx')}>
            <input type="text" className="mono" dir="ltr" value={sel.excludeExtensions.join(', ')} onChange={(e) => setSel({ excludeExtensions: exts(e.target.value) })} />
          </Field>
        </div>
        {children}
      </fieldset>
    </TechDetails>
  );
}

/** Whether the plan's history gets shorter, for the "older backups will be removed" warning. */
export const shortensHistory = (from: Retention, to: Retention) => historyDays(to) < historyDays(from);
